import { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';
import { format } from 'date-fns';
import { QrCode, Download, Calendar, Users, Edit, ChevronLeft, ChevronRight, MapPin, ExternalLink } from 'lucide-react';
import Logo from '../../components/Logo';

interface QRCodeItem {
  id: string;
  code: string;
  url: string;
  status: string;
  qrImagePng: string;
  createdAt: string;
  run: {
    id: string;
    instanceNumber: number;
    status: string;
    startDate: string;
    endDate: string;
    campaign: {
      id: string;
      name: string;
      description: string | null;
    };
  };
  _count: {
    scans: number;
  };
}

interface StoreProfile {
  name: string;
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  successMessage: string | null;
}

export default function QRCodesPage() {
  const [qrCodes, setQRCodes] = useState<QRCodeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [previewQR, setPreviewQR] = useState<QRCodeItem | null>(null);
  const [storeProfile, setStoreProfile] = useState<StoreProfile | null>(null);
  const [previewView, setPreviewView] = useState<'form' | 'success'>('form');

  useEffect(() => {
    loadQRCodes();
    loadStoreProfile();
  }, []);

  const loadStoreProfile = async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: StoreProfile }>('/store/profile');
      setStoreProfile(response.data.data);
    } catch (error) {
      console.error('Failed to load store profile:', error);
    }
  };

  const loadQRCodes = async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: QRCodeItem[] }>('/store/qrcodes');
      setQRCodes(response.data.data);
    } catch (error) {
      console.error('Failed to load QR codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      ACTIVE: 'bg-green-100 text-green-800',
      PAUSED: 'bg-yellow-100 text-yellow-800',
      DRAFT: 'bg-gray-100 text-gray-800',
      COMPLETED: 'bg-blue-100 text-blue-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const downloadPDF = async (qrCode: QRCodeItem) => {
    try {
      const blob = await apiClient.getBlob(`/store/qrcodes/${qrCode.id}/pdf`);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${qrCode.run.campaign.name}_Run${qrCode.run.instanceNumber}_QR.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">My QR Codes</h1>
      </div>

      {qrCodes.length === 0 ? (
        <div className="card text-center py-12">
          <QrCode className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No QR codes available yet.</p>
          <p className="text-sm text-gray-400 mt-2">
            QR codes will appear here when you're enrolled in an active campaign.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {qrCodes.map((qr) => (
            <div key={qr.id} className="card hover:shadow-lg transition-shadow">
              {/* QR Code Preview */}
              <div className="flex justify-center mb-4">
                <div className="w-32 h-32 bg-white border-2 border-gray-100 rounded-lg overflow-hidden">
                  {qr.qrImagePng ? (
                    <img
                      src={qr.qrImagePng}
                      alt={`QR Code for ${qr.run.campaign.name}`}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-50">
                      <QrCode className="w-12 h-12 text-gray-300" />
                    </div>
                  )}
                </div>
              </div>

              {/* Campaign Info */}
              <div className="text-center mb-4">
                <h3 className="font-semibold text-gray-900">{qr.run.campaign.name}</h3>
                <p className="text-sm text-gray-500">Run #{qr.run.instanceNumber}</p>
              </div>

              {/* Status Badge */}
              <div className="flex justify-center mb-4">
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusBadge(qr.run.status)}`}>
                  {qr.run.status}
                </span>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{format(new Date(qr.run.startDate), 'MMM d')} - {format(new Date(qr.run.endDate), 'MMM d')}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Users className="w-4 h-4" />
                  <span>{qr._count.scans} scans</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => setPreviewQR(qr)}
                  className="flex-1 btn-secondary text-sm flex items-center justify-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  View
                </button>
                <button
                  onClick={() => downloadPDF(qr)}
                  className="flex-1 btn-primary text-sm flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* QR Preview Modal */}
      {previewQR && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => { setPreviewQR(null); setPreviewView('form'); }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Left side - QR Code info */}
              <div className="lg:w-1/2">
                <div className="text-center lg:text-left">
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    {previewQR.run.campaign.name}
                  </h2>
                  <p className="text-sm text-gray-500 mb-4">
                    Run #{previewQR.run.instanceNumber}
                  </p>

                  {previewQR.qrImagePng && (
                    <img
                      src={previewQR.qrImagePng}
                      alt="QR Code"
                      className="w-48 h-48 mx-auto lg:mx-0 rounded-lg border border-gray-200"
                    />
                  )}

                  <p className="text-sm text-gray-500 mt-4 break-all">
                    {previewQR.url}
                  </p>

                  <div className="mt-4 flex flex-wrap gap-4 text-sm justify-center lg:justify-start">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>{format(new Date(previewQR.run.startDate), 'MMM d')} - {format(new Date(previewQR.run.endDate), 'MMM d, yyyy')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Users className="w-4 h-4" />
                      <span>{previewQR._count.scans} scans</span>
                    </div>
                  </div>

                  <div className="mt-6 flex gap-3 justify-center lg:justify-start">
                    <button
                      onClick={() => downloadPDF(previewQR)}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Download className="w-4 h-4" />
                      Download PDF
                    </button>
                    <button
                      onClick={() => { setPreviewQR(null); setPreviewView('form'); }}
                      className="btn-secondary"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>

              {/* Right side - Phone Preview */}
              <div className="lg:w-1/2">
                <div className="text-center mb-4">
                  <p className="text-sm text-gray-500 mb-3">
                    This is what customers see on their phone when they scan the QR code.
                  </p>
                  {/* Toggle Buttons */}
                  <div className="flex items-center justify-center gap-2">
                    <button
                      onClick={() => setPreviewView('form')}
                      disabled={previewView === 'form'}
                      className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <span className="text-sm font-medium text-gray-700 min-w-[120px]">
                      {previewView === 'form' ? 'Registration Form' : 'Success Page'}
                    </span>
                    <button
                      onClick={() => setPreviewView('success')}
                      disabled={previewView === 'success'}
                      className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Phone Frame */}
                <div className="mx-auto w-[280px] h-[560px] bg-gray-900 rounded-[3rem] p-3 shadow-xl">
                  <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                    {/* Notch */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-900 rounded-b-xl z-10"></div>

                    {/* Screen Content */}
                    <div className="w-full h-full overflow-y-auto">
                      {previewView === 'form' ? (
                        /* Form View */
                        <div className="min-h-full" style={{ backgroundColor: storeProfile?.secondaryColor || '#f9fafb' }}>
                          {/* Header */}
                          <div
                            className="p-4 pt-8 text-center"
                            style={{ backgroundColor: storeProfile?.primaryColor || '#22c55e' }}
                          >
                            {storeProfile?.logoUrl && (
                              <img
                                src={storeProfile.logoUrl}
                                alt={storeProfile.name}
                                className="h-10 mx-auto mb-2"
                              />
                            )}
                            <h1 className="text-lg font-bold text-white">{storeProfile?.name || 'Your Store'}</h1>
                          </div>

                          {/* Campaign info */}
                          <div className="p-4 text-center">
                            <h2 className="text-base font-bold text-gray-900 mb-1">{previewQR.run.campaign.name}</h2>
                            {previewQR.run.campaign.description && (
                              <p className="text-xs text-gray-600">{previewQR.run.campaign.description}</p>
                            )}
                          </div>

                          {/* Form */}
                          <div className="px-4 pb-4">
                            <div className="bg-white rounded-xl shadow-lg p-4">
                              <h3 className="text-sm font-bold text-gray-900 mb-3 text-center">
                                Enter the Raffle!
                              </h3>
                              <div className="space-y-3">
                                <div>
                                  <label className="text-xs text-gray-600">Your Name</label>
                                  <div className="mt-1 h-8 bg-gray-100 rounded border border-gray-200"></div>
                                </div>
                                <div>
                                  <label className="text-xs text-gray-600">Email Address</label>
                                  <div className="mt-1 h-8 bg-gray-100 rounded border border-gray-200"></div>
                                </div>
                                <div
                                  className="h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold"
                                  style={{ backgroundColor: storeProfile?.primaryColor || '#22c55e' }}
                                >
                                  Enter Raffle
                                </div>
                              </div>
                              <p className="text-[10px] text-gray-400 text-center mt-3">
                                Each scan = 1 raffle entry. Visit more stores for more chances to win!
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        /* Success View */
                        <div
                          className="min-h-full flex flex-col items-center justify-center p-4 pt-8"
                          style={{ backgroundColor: storeProfile?.primaryColor || '#22c55e' }}
                        >
                          {/* Header */}
                          <div className="flex items-center gap-2 mb-4">
                            <Logo size="sm" className="drop-shadow-lg" />
                            <div className="text-white">
                              <h2 className="text-sm font-bold leading-tight">Discover</h2>
                              <h2 className="text-sm font-bold leading-tight">the Park</h2>
                            </div>
                          </div>

                          {/* Campaign Badge */}
                          <div className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 mb-4">
                            <span className="text-white text-xs font-medium">{previewQR.run.campaign.name}</span>
                          </div>

                          <div className="text-center text-white">
                            <div className="text-4xl mb-2">🎉</div>
                            <h1 className="text-xl font-bold mb-2">You're In!</h1>
                            <p className="text-sm mb-2">
                              You now have <span className="font-bold text-2xl">3</span> entries in the raffle!
                            </p>
                            <p className="text-white/90 text-xs mb-4">
                              {storeProfile?.successMessage || `You will be contacted by email if you won a prize on or about ${previewQR?.run?.endDate ? format(new Date(previewQR.run.endDate), 'MMMM d, yyyy') : '[End Date]'}.`}
                            </p>

                            {/* Mini Map Preview */}
                            <div className="mt-4 pt-4 border-t border-white/20">
                              <p className="text-white/90 text-xs mb-3">
                                Visit other stores for more chances to win!
                              </p>
                              <div className="bg-white rounded-lg p-2 shadow-lg">
                                <div className="bg-gray-200 rounded h-24 flex items-center justify-center">
                                  <div className="text-center">
                                    <MapPin className="w-6 h-6 text-primary-600 mx-auto" />
                                    <p className="text-[10px] text-gray-600 mt-1">View Map</p>
                                  </div>
                                </div>
                              </div>
                              <button className="mt-3 inline-flex items-center gap-1 px-3 py-2 bg-white text-gray-900 rounded-lg text-xs font-semibold">
                                <MapPin className="w-3 h-3" />
                                View Other Stores
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
